import { useMemo } from 'react';

import type { Incident } from 'components/IAP/hooks/incidentHooks';
import type { AreaOfResponsibility } from 'types/aor';

export type ReportsFilterSelection = {
  value: UUID;
  label: string;
  kind: 'aor' | 'incident';
};

export type ReportsFilterOptionGroup = {
  label: string;
  options: ReportsFilterSelection[];
};

type UseReportsFilterOptionsArgs = {
  aors: AreaOfResponsibility[];
  incidents: Incident[];
  selectedFilter: ReportsFilterSelection | null;
};

export const useReportsFilterOptions = ({
  aors,
  incidents,
  selectedFilter,
}: UseReportsFilterOptionsArgs) => {
  const aorOptions = useMemo(
    () =>
      aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
        kind: 'aor' as const,
      })),
    [aors]
  );

  const incidentOptions = useMemo(
    () =>
      incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
        kind: 'incident' as const,
      })),
    [incidents]
  );

  const groupedOptions = useMemo<ReportsFilterOptionGroup[]>(
    () => [
      {
        label: 'AORs',
        options: aorOptions,
      },
      {
        label: 'Incidents',
        options: incidentOptions,
      },
    ],
    [aorOptions, incidentOptions]
  );

  const selectedOption = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const matchingOptions =
      selectedFilter.kind === 'incident' ? incidentOptions : aorOptions;

    return (
      matchingOptions.find((option) => option.value === selectedFilter.value) ??
      selectedFilter
    );
  }, [aorOptions, incidentOptions, selectedFilter]);

  return {
    groupedOptions,
    selectedOption,
  };
};
